import {useManageMedicalConsentsMutation} from '../../../../api/services/lookupsService';
import {Alert, Box, Button} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useRef, useState} from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import EditIcon from '@mui/icons-material/Edit';
import useNotification from '../../../../utility/hooks/useNotification';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DescriptionSettings from './DescriptionSettings';
import SectionHeading from './SectionHeading';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

function MedicalConsentSettings({
    medicalConsentOptions,
    title,
    description,
    onTitleChange,
    onDescriptionChange
}) {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const formRef = useRef(null);
    const [selectedConsent, setSelectedConsent] = useState({});
    const [activeConsents, setActiveConsents] = useState([]);
    const [saveValidationError, setSaveValidationError] = useState('');
    const {showErrorNotification, showSuccessNotification} = useNotification();

    const [manageConsents, {
        isSuccess,
        isError,
    }] = useManageMedicalConsentsMutation();

    useEffect(() => {
        setActiveConsents(medicalConsentOptions?.options);
    }, [medicalConsentOptions]);

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update medical consents');
        } else if (isSuccess) {
            showSuccessNotification(
                'Medical consents updated successfully. The Groups filter tree will reflect these changes after the next MIS synchronization.'
            );
        }
    }, [isSuccess, isError]);

    const normalizeTitle = (value) => value?.trim().toLowerCase();

    const getDuplicateTitleMessage = (value) => {
        const normalizedCurrent = normalizeTitle(value);
        if (!normalizedCurrent) {
            return '';
        }

        const duplicateCount = activeConsents.filter(consent => {
            const normalizedExisting = normalizeTitle(consent?.description);
            if (normalizedExisting !== normalizedCurrent) {
                return false;
            }

            return openAddDialog || consent?.description !== selectedConsent?.description;
        }).length;

        return duplicateCount > 0 ? 'This title already exists. Please use a unique title.' : '';
    };

    const textFields = [{
        label: 'Title',
        name: 'description',
        required: true,
        initialValue: selectedConsent?.description ?? undefined,
        validate: (value) => {
            const duplicateTitleMessage = getDuplicateTitleMessage(value);
            return {
                isValid: !!value && !!value.trim() && value.length <= 300 && !duplicateTitleMessage,
                message: getTitleValidationMessage(value, duplicateTitleMessage)
            };
        }
    }, {
        label: 'Summary',
        name: 'summary',
        required: false,
        multiline: true,
        rows: 3,
        nullableOnEmpty: true,
        initialValue: selectedConsent?.summary ?? undefined,
        validate: (value) => {
            return {
                isValid: !value || value.length <= 500,
                message: 'Summary cannot exceed 500 characters.'
            };
        }
    }];

    const getTitleValidationMessage = (value, duplicateTitleMessage = '') => {
        if (!value || value.length === 0) {
            return 'Please provide a title.';
        }

        if (!value.trim()) {
            return 'Title cannot contain only spaces.';
        }

        if (value.length > 300) {
            return 'Title cannot exceed 300 characters.';
        }

        if (duplicateTitleMessage) {
            return duplicateTitleMessage;
        }

        if (value.trim().length > 0 && value.length <= 300) {
            return '';
        }

        return 'Please provide a title.';
    };

    const getListValidationError = () => {
        const normalizedTitles = new Set();

        for (const consent of activeConsents) {
            const title = consent?.description ?? '';

            if (!title.trim()) {
                return 'Each medical consent must have a title. Titles cannot contain only spaces.';
            }

            if (title.length > 300) {
                return 'Medical consent titles cannot exceed 300 characters.';
            }

            const normalizedTitle = normalizeTitle(title);

            if (normalizedTitles.has(normalizedTitle)) {
                return 'Duplicate medical consent titles are not allowed.';
            }

            normalizedTitles.add(normalizedTitle);
        }

        return '';
    };

    const deleteConsent = (description) => {
        setActiveConsents(activeConsents.filter(e => e.description !== description));
    };

    const moveConsent = (index, targetIndex) => {
        if (targetIndex < 0 || targetIndex >= activeConsents.length) {
            return;
        }

        const next = [...activeConsents];
        const [item] = next.splice(index, 1);
        next.splice(targetIndex, 0, item);
        setActiveConsents(next);
    };

    const handleAdd = async (payload) => {
        setActiveConsents([...activeConsents, {
            description: payload.description,
            summary: payload.summary,
            id: null
        }]);
        setSaveValidationError('');
        handleFinishEditing();
    };

    const startEditing = (consent) => {
        setSelectedConsent(consent);
        setOpenEditDialog(true);
    };

    const handleEdit = async (payload) => {
        const newConsents = activeConsents.map(consent => {
            if (consent.description === selectedConsent.description) {
                return {
                    ...consent,
                    description: payload.description,
                    summary: payload.summary
                };
            }
            return consent;
        });

        setActiveConsents(newConsents);
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleFinishEditing = () => {
        setOpenAddDialog(false);
        setOpenEditDialog(false);
        setSelectedConsent(undefined);
    };

    const handleSave = () => {
        const validationError = getListValidationError();
        setSaveValidationError(validationError);

        if (validationError) {
            return;
        }

        manageConsents({activeMedicalConsents: activeConsents});
    };

    return (
        <Box>
            <SectionHeading title={title} onSave={onTitleChange} testId="otc-heading"/>
            <DescriptionSettings
                description={description}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedication]}
                onDescriptionChange={onDescriptionChange}
            />
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => setOpenAddDialog(true)}
                >
                    Add new
                </Button>
            </Box>
            <List disablePadding sx={{width: '100%'}}>
                {
                    activeConsents.map((entity, index) =>
                        <ListItem
                            key={`medicalConsent-${index}`}
                            sx={{
                                px: 1,
                                py: 0.25,
                                backgroundColor: index % 2 === 0 ? '#F0FBFA' : '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#D4F4F0',
                                },
                            }}
                        >
                            <ListItemText
                                sx={{my: 0}}
                                primary={
                                    <Stack
                                        flexDirection="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Box>
                                            <Typography>{entity?.description}</Typography>
                                            {entity?.summary
                                                ? <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-line'}}>
                                                    {entity.summary}
                                                </Typography>
                                                : null}
                                        </Box>
                                        <Box display={'flex'}>
                                            <IconButton
                                                size="small"
                                                disabled={index === 0}
                                                onClick={() => moveConsent(index, index - 1)}
                                                aria-label="Move consent up"
                                            >
                                                <ArrowUpwardOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={index === activeConsents.length - 1}
                                                onClick={() => moveConsent(index, index + 1)}
                                                aria-label="Move consent down"
                                            >
                                                <ArrowDownwardOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small" onClick={() => startEditing(entity)}>
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small"
                                                        onClick={() => deleteConsent(entity.description)}>
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                }
                            />
                        </ListItem>)
                }
            </List>
            <Box
                display={'flex'}
                alignItems={'center'}
                gap={2}
                py={1}
                flexDirection={{xs: 'column', sm: 'row'}}
            >
                <Alert
                    severity="info"
                    sx={{
                        flexGrow: 1,
                        py: 0,
                        width: {xs: '100%', sm: 'auto'},
                        '& .MuiAlert-message': {fontSize: '0.75rem', py: 0.75}
                    }}
                >
                    Changes to Medical OTC consent options are saved when Save is selected. The Groups filter tree
                    will be refreshed with the updated values after the next MIS synchronization.
                </Alert>
                {saveValidationError
                    ? <Alert severity="error" sx={{width: {xs: '100%', sm: 'auto'}}}>
                        {saveValidationError}
                    </Alert>
                    : null}
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    onClick={handleSave}>
                    Save
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={handleFinishEditing}
                title={openAddDialog ? 'New Medical Consent' : 'Update Medical Consent'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={(data) => openAddDialog
                        ? handleAdd(data)
                        : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
}

MedicalConsentSettings.propTypes = {
    medicalConsentOptions: PropTypes.object,
    title: PropTypes.string,
    description: PropTypes.string,
    onTitleChange: PropTypes.func,
    onDescriptionChange: PropTypes.func
};

export default MedicalConsentSettings;
